<template>
    <BaseCard padding="lg" as="article" :class="showUnanswered ? 'ring-2 ring-inset ring-red-300' : ''" :style="accentStyle">
        <div v-if="question.title" class="details text-valence-grey-750 mb-2">{{ question.title }}</div>
        <h2 class="body-bold text-valence-grey-850 leading-snug">
            {{ question.text }}
            <span v-if="!question.is_optional" class="text-red-600 ml-0.5" aria-label="Required">*</span>
        </h2>

        <div v-if="question.is_score" class="mt-6">
            <div class="grid gap-2" :style="{ gridTemplateColumns: `repeat(${scaleSize}, minmax(0, 1fr))` }">
                <button
                    v-for="n in scaleSize"
                    :key="n"
                    type="button"
                    class="score-tile"
                    :class="{ 'score-tile--selected': score === n && !notObserved, 'score-tile--accented': accentColor }"
                    @click="$emit('update:score', n)">
                    {{ n }}
                </button>
            </div>
            <div class="grid mt-2 details text-valence-grey-750" :style="{ gridTemplateColumns: `repeat(${scaleSize}, minmax(0, 1fr))` }">
                <div v-for="(label, i) in question.scale_labels.labels" :key="i" :class="labelAlign(i)">{{ label }}</div>
            </div>
        </div>

        <div v-if="showComposer" :class="question.is_score ? 'mt-6 pt-5 border-t border-valence-cream-200' : 'mt-4'">
            <div v-if="question.is_score" class="body-small-semibold text-valence-grey-850 mb-2">
                Share a specific example <span class="body-small text-valence-grey-700">(optional)</span>
            </div>
            <RaterTextComposer v-model="textModel" :rows="4" @update:busy="$emit('update:busy', $event)" />
        </div>

        <div v-if="allowNotObserved && question.is_score" class="mt-4">
            <button
                type="button"
                class="not-observed-pill"
                :class="{ 'not-observed-pill--selected': notObserved, 'not-observed-pill--accented': accentColor }"
                :aria-pressed="notObserved"
                @click="$emit('update:notObserved', !notObserved)">
                <i class="bi" :class="notObserved ? 'bi-check-circle-fill' : 'bi-slash-circle'" aria-hidden="true"></i>
                {{ notObservedCaption }}
            </button>
        </div>
    </BaseCard>
</template>

<script setup lang="ts">
import type { ScaleLabels } from "~vue/api/generated/reflections";
import BaseCard from "~vue/components/BaseCard.vue";
import RaterTextComposer from "~vue/components/RaterTextComposer.vue";
import { computed } from "vue";

export interface QuestionCardItem {
    id: string;
    title?: string;
    text: string;
    is_score: boolean;
    is_text: boolean;
    is_optional?: boolean;
    scale_labels: ScaleLabels;
}

interface Props {
    question: QuestionCardItem;
    score?: number | null;
    text: string;
    notObserved?: boolean;
    showUnanswered?: boolean;
    // LAB-798: when false, the "Share a specific example" composer is hidden on
    // SCORE (Likert) questions; open-ended TEXT questions always keep it.
    allowScoreExamples?: boolean;
    // LAB-794: when false, the "Not applicable — I haven't observed this" opt-out
    // is hidden.
    allowNotObserved?: boolean;
    // LAB-988: optional accent color (CSS color) used for the selected-score
    // tile background and the "not observed" pill. When unset, callers keep the
    // legacy Valence primary (purple) styling. The three family colors —
    // amber #FBB877 (self), mint #bee59f (team), pink #e84c8d (360 subject) —
    // are non-token per project decision, so they're passed as raw hex here.
    accentColor?: string;
    // Pastel fill for the selected score tile — the saturated accent is too heavy
    // behind the tile number, so it stays on hover/focus only.
    accentSurfaceColor?: string;
}

const props = withDefaults(defineProps<Props>(), {
    notObserved: false,
    showUnanswered: false,
    allowScoreExamples: true,
    allowNotObserved: true,
});

// Open-ended questions always show their reflection composer; score questions
// only when examples are allowed for the template.
const showComposer = computed(() => !props.question.is_score || props.allowScoreExamples);

const accentStyle = computed(() => {
    if (!props.accentColor) {
        return undefined;
    }
    return {
        "--question-accent": props.accentColor,
        "--question-accent-surface": props.accentSurfaceColor ?? props.accentColor,
    };
});

const emit = defineEmits<{
    "update:score": [number];
    "update:text": [string];
    "update:notObserved": [boolean];
    "update:busy": [boolean];
}>();

const scaleSize = computed(() => props.question.scale_labels.labels.length);
// LAB-878: some scales rename the opt-out (e.g. `effectiveness_4` uses
// "Not Observed / Not Applicable", `frequency_3` uses "Cannot Rate"). Falls
// back to the generic "Not applicable" copy for scales without an override.
const notObservedCaption = computed(() => {
    return props.question.scale_labels.not_observed_label ?? "Not applicable — I haven't observed this";
});

const textModel = computed({
    get: () => props.text,
    set: (v: string) => emit("update:text", v),
});

// Align the leftmost non-empty label left and the rightmost non-empty label
// right, matching how Image 15 anchors "Rarely" / "Consistently" to the ends.
function labelAlign(i: number): string {
    const labels = props.question.scale_labels.labels;
    if (i === 0 && labels[i]) {
        return "text-left";
    }
    if (i === labels.length - 1 && labels[i]) {
        return "text-right";
    }
    return "text-center";
}
</script>

<style scoped>
@reference "../../../css/tailwind.css";

@layer components {
    .score-tile {
        @apply flex items-center justify-center py-4 sm:py-5 rounded-lgr border border-valence-cream-300 bg-white body-bold text-valence-grey-850 transition-colors hover:border-valence-primary-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-valence-primary-300;
    }

    .score-tile--selected {
        @apply bg-valence-primary-500 border-valence-primary-500 text-white;
    }

    /* LAB-988: when the parent supplies --question-accent, the selected tile
       and hover/focus rings switch to the tab family color (amber / mint /
       pink) instead of Valence primary purple. Selected text stays dark so the
       amber/mint pastels keep enough contrast. */
    .score-tile--accented {
        @apply hover:border-[color:var(--question-accent)] focus-visible:ring-[color:var(--question-accent)];
    }
    .score-tile--accented.score-tile--selected {
        background-color: var(--question-accent-surface);
        border-color: var(--question-accent-surface);
        color: var(--color-valence-grey-850);
    }

    .not-observed-pill {
        @apply inline-flex items-center gap-2 rounded-full border border-valence-cream-300 bg-white px-4 py-2 body-small text-valence-grey-750 hover:border-valence-primary-300 hover:text-valence-grey-850 focus:outline-none focus-visible:ring-2 focus-visible:ring-valence-primary-300;
    }

    .not-observed-pill--selected {
        @apply bg-valence-primary-100 border-valence-primary-500 text-valence-primary-600;
    }

    .not-observed-pill--accented {
        @apply hover:border-[color:var(--question-accent)] focus-visible:ring-[color:var(--question-accent)];
    }
    .not-observed-pill--accented.not-observed-pill--selected {
        background-color: color-mix(in srgb, var(--question-accent) 25%, white);
        border-color: var(--question-accent);
        color: var(--color-valence-grey-850);
    }
}
</style>
